// src/components/museumGuide/MuseumGuideView.tsx
//
// Top-half view rendered above the chat when a conversation is in
// museum_guide mode. Indoor cousin of WalkingTourView. No map — we
// can't render a useful one without floor plans, so this is a
// scrollable exhibit list (guided) or a free-roam status panel.
//
//   ┌─────────────────────────┐
//   │ Tate Modern · Free roam │  ← header strip
//   │ 7 photos so far         │
//   ├─────────────────────────┤
//   │   (chat below)          │  ← rendered by parent
//   └─────────────────────────┘
//
// In guided mode the strip becomes a vertical stack of exhibits that
// the user marks as visited or skips. The current exhibit is anchored
// at the top and the "I've seen it" / "Skip" controls live there.

import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  AccountBalanceOutlined,
  CheckCircle,
  CheckCircleOutlined,
  RadioButtonUncheckedOutlined,
  SkipNextOutlined,
  PhotoCameraOutlined,
} from '@mui/icons-material';
import {
  doc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import type {
  Conversation,
  MuseumGuideExhibit,
  MuseumGuideExhibitStatus,
} from '../../stores/conversationStore';
import { useT } from '../../i18n/useT';

interface Props {
  conversation: Conversation;
}

const STATUS_COLOR: Record<
  MuseumGuideExhibitStatus,
  'success' | 'warning' | 'default' | 'primary'
> = {
  visited: 'success',
  current: 'warning',
  skipped: 'default',
  pending: 'primary',
};

export const MuseumGuideView: React.FC<Props> = ({ conversation }) => {
  const t = useT();
  const mg = conversation.museum_guide;

  // During AI-driven onboarding (state machine asking the user for
  // the museum or guided/free-roam choice), there's no panel to
  // show — the chat below is hosting an interactive Q&A. Return
  // null and let the message list use the full screen until the
  // conversation flips to a populated mode.
  if (
    !mg ||
    (mg as any).status === 'awaiting_museum' ||
    (mg as any).status === 'awaiting_submode'
  ) {
    return null;
  }

  // Loader / error states. Mirrors the walking-tour generating state —
  // the user sees something happening while the highlight list is
  // being composed.
  if (mg.status === 'generating') {
    return (
      <Box
        sx={{
          height: '38vh',
          minHeight: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          px: 3,
          textAlign: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          {t('Picking the must-see exhibits…')}
        </Typography>
      </Box>
    );
  }
  if (mg.status === 'error') {
    return (
      <Box
        sx={{
          py: 2,
          px: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body2" color="error">
          {t('Couldn’t set up the museum guide.')} {mg.last_error || ''}
        </Typography>
      </Box>
    );
  }

  // ── Free roam: simple status header ────────────────────────────────
  if (mg.mode === 'free_roam') {
    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <AccountBalanceOutlined color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {mg.museum_name || t('Museum')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('Free roam')}
            {' · '}
            {mg.observations_count
              ? t('{{count}} photos so far').replace(
                  '{{count}}',
                  String(mg.observations_count)
                )
              : t('Snap a photo to start')}
          </Typography>
        </Box>
        <Tooltip title={t('Snap an exhibit')}>
          <IconButton
            color="primary"
            // The actual camera button is in the composer below;
            // tapping this is a visual reminder rather than a
            // separate camera path. We don't open a duplicate camera
            // dialog here — that would mean two cameras to keep in
            // sync. Instead, click is a no-op visual cue.
            disableRipple
          >
            <PhotoCameraOutlined />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // ── Guided: exhibit list ───────────────────────────────────────────
  return (
    <GuidedExhibitList conversation={conversation} />
  );
};

// ─────────────────────────────────────────────────────────────────────
// Guided mode rendering split out so the free-roam path doesn't have
// to compute exhibit indices it doesn't need.

const GuidedExhibitList: React.FC<Props> = ({ conversation }) => {
  const t = useT();
  const mg = conversation.museum_guide!;
  const exhibits = useMemo<MuseumGuideExhibit[]>(
    () => mg.exhibits || [],
    [mg.exhibits]
  );
  const currentIdx =
    typeof mg.current_exhibit_index === 'number'
      ? mg.current_exhibit_index
      : 0;
  const current = exhibits[currentIdx];
  const visitedCount = exhibits.filter((e) => e.status === 'visited').length;
  const isComplete = !!mg.completed_at || currentIdx >= exhibits.length;

  // Persist mutations directly to Firestore. Same approach as
  // WalkingTourView — keeps state in one place (the conversation doc),
  // and the snapshot listener re-renders us.
  const writeExhibitChange = useCallback(
    async (
      idx: number,
      patch: Partial<MuseumGuideExhibit>,
      nextIdx?: number
    ) => {
      if (!auth.currentUser) return;
      const next = exhibits.map((e, i) => (i === idx ? { ...e, ...patch } : e));
      const update: any = {
        'museum_guide.exhibits': next,
        updated_at: serverTimestamp(),
      };
      if (typeof nextIdx === 'number') {
        update['museum_guide.current_exhibit_index'] = nextIdx;
        // Mark the new current exhibit
        if (nextIdx >= 0 && nextIdx < next.length) {
          next[nextIdx] = { ...next[nextIdx], status: 'current' };
          update['museum_guide.exhibits'] = next;
        }
        // If we ran past the end, mark complete
        if (nextIdx >= next.length) {
          update['museum_guide.completed_at'] = Timestamp.now();
        }
      }
      try {
        await updateDoc(doc(db, 'conversations', conversation.id), update);
      } catch (err) {
        console.warn('museum_guide write failed:', err);
      }
    },
    [conversation.id, exhibits]
  );

  const handleMarkVisited = useCallback(() => {
    if (!current) return;
    writeExhibitChange(
      currentIdx,
      { status: 'visited', visited_at: new Date() as any },
      currentIdx + 1
    );
  }, [current, currentIdx, writeExhibitChange]);

  const handleSkip = useCallback(() => {
    if (!current) return;
    writeExhibitChange(currentIdx, { status: 'skipped' }, currentIdx + 1);
  }, [current, currentIdx, writeExhibitChange]);

  const handleJump = useCallback(
    (idx: number) => {
      // Move "current" pointer forward or back. Don't auto-mark
      // skipped on backward jumps — the user might just be glancing.
      if (idx === currentIdx) return;
      const next = exhibits.map((e, i) => {
        if (i === idx) return { ...e, status: 'current' as const };
        if (i === currentIdx && e.status === 'current') {
          return { ...e, status: 'pending' as const };
        }
        return e;
      });
      void updateDoc(doc(db, 'conversations', conversation.id), {
        'museum_guide.exhibits': next,
        'museum_guide.current_exhibit_index': idx,
        updated_at: serverTimestamp(),
      }).catch((err) => console.warn('museum_guide jump failed:', err));
    },
    [conversation.id, exhibits, currentIdx]
  );

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0,
        // Cap the panel — exhibit lists can run 10+ items, we don't
        // want it eating the whole screen.
        maxHeight: '45vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header strip */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceOutlined color="primary" fontSize="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {mg.museum_name || t('Museum')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isComplete
              ? t('Tour complete · {{n}} stops').replace(
                  '{{n}}',
                  String(exhibits.length)
                )
              : t('Stop {{i}} of {{n}}')
                  .replace('{{i}}', String(currentIdx + 1))
                  .replace('{{n}}', String(exhibits.length))}
            {' · '}
            {t('{{v}} seen').replace('{{v}}', String(visitedCount))}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Current exhibit + controls */}
      {!isComplete && current && (
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="overline" color="warning.main" sx={{ fontWeight: 700 }}>
            {t('Up next')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.25 }}>
            {current.title}
          </Typography>
          {current.hint && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {current.hint}
            </Typography>
          )}
          {current.why_visit && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {current.why_visit}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Button
              size="small"
              variant="contained"
              onClick={handleMarkVisited}
              startIcon={<CheckCircle />}
              sx={{ textTransform: 'none' }}
            >
              {t("I've seen it")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSkip}
              startIcon={<SkipNextOutlined />}
              sx={{ textTransform: 'none' }}
            >
              {t('Skip')}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Compact list of all exhibits — scrollable.
          Tap any pending or visited row to jump the "current" pointer
          there. Useful if the user wandered out of order. */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Stack divider={<Divider component="div" flexItem />}>
          {exhibits.map((e, i) => {
            const status = e.status || 'pending';
            const isCurrent = i === currentIdx && !isComplete;
            return (
              <Box
                key={i}
                onClick={() => handleJump(i)}
                role="button"
                tabIndex={0}
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  bgcolor: isCurrent ? 'action.selected' : 'transparent',
                }}
              >
                {status === 'visited' ? (
                  <CheckCircleOutlined fontSize="small" color="success" />
                ) : (
                  <RadioButtonUncheckedOutlined
                    fontSize="small"
                    color={status === 'current' ? 'warning' : 'disabled'}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: isCurrent ? 600 : 400,
                    color:
                      status === 'skipped'
                        ? 'text.secondary'
                        : 'text.primary',
                    textDecoration:
                      status === 'skipped' ? 'line-through' : 'none',
                  }}
                >
                  {e.title}
                </Typography>
                <Chip
                  size="small"
                  label={
                    status === 'visited'
                      ? t('Seen')
                      : status === 'current'
                      ? t('Now')
                      : status === 'skipped'
                      ? t('Skipped')
                      : `${i + 1}`
                  }
                  color={STATUS_COLOR[status]}
                  variant={status === 'pending' ? 'outlined' : 'filled'}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};
